import { useEffect, useMemo, useState } from "react";
import { dateLocale } from "@/lib/dateTime";
import { Link } from "react-router-dom";
import { Person } from "@/components/patterns";
import { fetchAllRows, EXPORT_LIMIT } from "@/lib/exportCsv";
import { formatINRCompact } from "@/lib/utils";
import type { DealRecord } from "@/types";

interface Group {
  key: string;
  label: string;
  deals: DealRecord[];
  danger?: boolean;
}

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/**
 * Open deals by the month they are expected to close, with the amount and the probability-weighted
 * forecast for each month. Overdue deals and deals with no date get their own groups.
 */
export function DealsForecast({ query, refreshKey, onError }: { query: string; refreshKey: number; onError: (m: string) => void }) {
  const [deals, setDeals] = useState<DealRecord[] | null>(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let alive = true;
    setDeals(null);
    fetchAllRows<DealRecord>("/api/Deal/List", query)
      .then((r) => {
        if (!alive) return;
        setDeals(r.rows);
        setTotal(r.total);
      })
      .catch((e) => alive && (onError(e?.message || "Could not load the forecast"), setDeals([])));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, refreshKey]);

  const groups = useMemo<Group[]>(() => {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    const open = (deals || []).filter((d) => d.deal_status === "OPEN");
    const by = new Map<string, Group>();
    const add = (key: string, label: string, d: DealRecord, danger = false) => {
      if (!by.has(key)) by.set(key, { key, label, deals: [], danger });
      by.get(key)!.deals.push(d);
    };
    for (const d of open) {
      if (!d.expected_close_date) {
        add("zz-none", "No close date", d);
        continue;
      }
      const when = new Date(d.expected_close_date);
      if (when < today) add("0-overdue", "Overdue", d, true);
      else add(monthKey(when), when.toLocaleDateString(dateLocale(), { month: "long", year: "numeric" }), d);
    }
    return [...by.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [deals]);

  if (deals === null) return <p className="py-10 text-center text-sm text-muted-foreground">Loading the forecast…</p>;
  if (groups.length === 0) return <p className="rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">No open deals match the filters.</p>;

  const sum = (rows: DealRecord[], k: "amount" | "weighted_amount") => rows.reduce((t, d) => t + Number(d[k] || 0), 0);
  const all = groups.flatMap((g) => g.deals);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
        <span>
          Open <strong className="font-mono text-foreground">{formatINRCompact(sum(all, "amount"))}</strong>
        </span>
        <span>
          Weighted <strong className="font-mono text-foreground">{formatINRCompact(sum(all, "weighted_amount"))}</strong>
        </span>
        {total > (deals?.length ?? 0) && <span className="text-warning">Showing the first {EXPORT_LIMIT} of {total} deals</span>}
      </div>
      <div className="grid grid-cols-1 gap-3 @4xl/main:grid-cols-2 @6xl/main:grid-cols-3">
        {groups.map((g) => (
          <section key={g.key} className="flex flex-col rounded-lg border border-border bg-card">
            <header className="flex items-baseline justify-between gap-2 border-b border-border px-3.5 py-2.5">
              <h2 className={`text-[13px] font-semibold ${g.danger ? "text-danger" : ""}`}>
                {g.label} <span className="ml-1 font-mono font-normal text-muted-foreground">{g.deals.length}</span>
              </h2>
              <span className="text-right">
                <span className="block font-mono text-[13px] font-semibold">{formatINRCompact(sum(g.deals, "amount"))}</span>
                <span className="block font-mono text-[11px] text-muted-foreground">weighted {formatINRCompact(sum(g.deals, "weighted_amount"))}</span>
              </span>
            </header>
            <ul className="max-h-[360px] divide-y divide-border/60 overflow-y-auto">
              {g.deals.map((d) => (
                <li key={d.id}>
                  <Link to={`/deals/${d.id}`} className="flex items-center gap-2 px-3.5 py-2 hover:bg-muted/50">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium">{d.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {d.account?.name || "—"} · {d.stage?.name || ""} · {d.expected_close_date ? new Date(d.expected_close_date).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" }) : "no date"}
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="block font-mono text-[13px] font-semibold">{formatINRCompact(d.amount)}</span>
                      <span className="block font-mono text-[11px] text-muted-foreground">{d.probability}%</span>
                    </span>
                    <Person name={d.owner ? `${d.owner.first_name}` : ""} className="hidden xl:inline-flex" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
